import { TextField, Button, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { NEW_CATEGORY_RESET } from '../../constants/categoryConstants';
import { getCategories, createCategory, clearErrors } from '../../actions/categoryAction';
import ImageIcon from '@mui/icons-material/Image';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme, styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const NewCategory = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();

    const { loading, success, error } = useSelector((state) => state.newCategory);
    const { categories } = useSelector((state) => state.categories);

    const [name, setName] = useState("");
    const [parent, setParent] = useState("");
    const [logo, setLogo] = useState("");
    const [logoPreview, setLogoPreview] = useState("");

    const handleLogoChange = (e) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setLogoPreview(reader.result);
                setLogo(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    }

    const newCategorySubmitHandler = (e) => {
        e.preventDefault();

        if (!name.trim()) {
            enqueueSnackbar("Name is required", { variant: "warning" });
            return;
        }

        dispatch(createCategory({ name, parent, logo }));
    }

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar("Category Created Successfully", { variant: "success" });
            dispatch({ type: NEW_CATEGORY_RESET });
            navigate("/admin/categories");
        }
        dispatch(getCategories());
    }, [dispatch, error, success, navigate, enqueueSnackbar]);

    return (
        <>
            <MetaData title="Admin: New Category | Flipkart" />

            {loading && <BackdropLoader />}
            <motion.form
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={newCategorySubmitHandler}
                encType="multipart/form-data"
                className="flex flex-col sm:flex-row rounded-lg shadow p-4"
                style={{ backgroundColor: theme.palette.background.paper }}
            >

                <div className="flex flex-col gap-3 m-2 sm:w-1/2">
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel id="parent-category-select-label">Parent Category</InputLabel>
                        <Select
                            labelId="parent-category-select-label"
                            id="parent-category-select"
                            value={parent}
                            label="Parent Category"
                            onChange={(e) => setParent(e.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {categories && categories.map((category) => (
                                <MenuItem key={category._id} value={category._id}>{category.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 100, height: 100, border: '2px dashed grey', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {logoPreview ? 
                                <img draggable="false" src={logoPreview} alt="Category Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : 
                                <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                            }
                        </Box>
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<ImageIcon />}
                        >
                            Upload Logo
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleLogoChange} />
                        </Button>
                    </Box>

                    <div className="flex justify-end">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                type="submit"
                                size="large"
                                sx={{ bgcolor: theme.palette.primary.main, width: '10rem' }}
                            >
                                Submit
                            </Button>
                        </motion.div>
                    </div>

                </div>

            </motion.form>
        </>
    );
};

export default NewCategory;
